'use client'

import { useBreakpoint } from '@qr-front/ui/hooks'
import { cn } from '@qr-front/ui/utils'
import {
	getCoreRowModel,
	getFilteredRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	Table as TanstackTable,
	useReactTable,
} from '@tanstack/react-table'
import { TablePagination } from 'features/shared/client'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { useHydrateAtoms } from 'jotai/utils'
import { useLocale, useTranslations } from 'next-intl'
import { useEffect } from 'react'

import { NEW_USER_COOKIE } from '@/constants'
import type { QR_TYPE } from '@/generator/shared'
import { getQrTypeTranslation, TQrData } from '@/generator/shared'
import { userAtom } from '@/global-state'
import { getTrialRemainingTime, isUserOnFreeTrial } from '@/utils'

import { deleteCookieClient } from '../../../../../utils/client'
import { DownloadQrDialog } from '../../dialogs/DownloadQr/DownloadQrDialog'
import { BulkActionsToolbar } from './BulkActionsToolbar/BulkActionsToolbar'
import { columns } from './columnsConfig'
import { ConfirmationDialog } from './dialogs/ConfirmationDialog'
import { DesktopActionsDialog } from './dialogs/DesktopActionsDialog'
import { EditQrNameDialog } from './dialogs/EditQrNameDialog'
import { EditQrUrlDialog } from './dialogs/EditQrUrlDialog'
import { MobileActionsDialog } from './dialogs/MobileActionsDialog'
import { QrPasswordDialogs } from './dialogs/QrPasswordDialogs'
import { ScanQrDialog } from './dialogs/ScanQrDialog'
import { ShareQrDialog } from './dialogs/ShareQrDialog'
import { EmptyState } from './EmptyState/EmptyState'
import { NoCodesState } from './NoCodesState/NoCodesState'
import { TableControls } from './TableControls/TableControls'
import { TableRowDesktop } from './TableRow/TableRowDesktop'
import { TableRowMobile } from './TableRow/TableRowMobileAndTablet/TableRowMobile'
import { TableRowTablet } from './TableRow/TableRowMobileAndTablet/TableRowTablet'
import {
	duplicatedQrHighlightAtom,
	qrListAtom,
	RowProvider,
	tableAtom,
	tableColumnFiltersAtom,
	tableSelectionAtom,
	tableSortingAtom,
} from './tableState'
import { compareSortValues } from './utils/localeSort'
import { scansNumericSort } from './utils/scansNumericSort'
import { Virtualizer } from './Virtualizer/Virtualizer'

const ROW_HEIGHT_ESTIMATE = 88
const VIRTUALIZER_OVERSCAN = 10

const DesktopTableVirtualizedContent = ({
	table,
}: {
	table: TanstackTable<TQrData>
}) => {
	const rows = table.getRowModel().rows
	const highlightId = useAtomValue(duplicatedQrHighlightAtom)

	return (
		<Virtualizer
			rows={rows}
			estimateSize={ROW_HEIGHT_ESTIMATE}
			overscan={VIRTUALIZER_OVERSCAN}
			as="table"
			renderRow={(row, index, measure) => {
				const refCallback = (rowItem: HTMLElement | null) => {
					measure(rowItem)

					if (rowItem && row.original.id === highlightId && index === 0) {
						rowItem.style.scrollMarginTop = '50px'

						rowItem.scrollIntoView({
							behavior: 'smooth',
							block: 'start',
						})

						setTimeout(() => {
							rowItem.style.scrollMarginTop = ''
						}, 1000)
					}
				}

				return (
					<RowProvider key={row.id} value={row}>
						<tr
							ref={refCallback}
							data-index={index}
							data-state={row.getIsSelected() && 'selected'}
							className={cn(
								'hidden w-full rounded-10 bg-white desktop:table-row',
								index === 0 &&
									row.original.id === highlightId &&
									'ring ring-inset ring-blue animate-in fade-in duration-300',
							)}
						>
							<TableRowDesktop />
						</tr>
					</RowProvider>
				)
			}}
		/>
	)
}

const MobileAndTabletVirtualizedContent = ({
	table,
}: {
	table: TanstackTable<TQrData>
}) => {
	const rows = table.getRowModel().rows
	const highlightId = useAtomValue(duplicatedQrHighlightAtom)

	return (
		<Virtualizer
			rows={rows}
			estimateSize={ROW_HEIGHT_ESTIMATE}
			overscan={VIRTUALIZER_OVERSCAN}
			as="ul"
			renderRow={(row, index, measure) => {
				const refCallback = (rowItem: HTMLElement | null) => {
					measure(rowItem)

					if (rowItem && row.original.id === highlightId && index === 0) {
						setTimeout(() => {
							const elementTop =
								rowItem.getBoundingClientRect().top + window.pageYOffset
							const scrollPosition = elementTop - 80

							window.scrollTo({
								top: scrollPosition,
								behavior: 'smooth',
							})
						}, 0)
					}
				}
				return (
					<RowProvider key={row.id} value={row}>
						<li
							ref={refCallback}
							data-index={index}
							data-state={row.getIsSelected() && 'selected'}
						>
							<TableRowMobile index={index} />
							<TableRowTablet index={index} />
						</li>
					</RowProvider>
				)
			}}
		/>
	)
}

export const Table = () => {
	const [data, setData] = useAtom(qrListAtom)
	const [rowSelection, setRowSelection] = useAtom(tableSelectionAtom)
	const [columnFilters, setColumnFilters] = useAtom(tableColumnFiltersAtom)
	const [sorting, setSorting] = useAtom(tableSortingAtom)
	const setTableAtom = useSetAtom(tableAtom)
	const user = useAtomValue(userAtom)
	const t = useTranslations()
	const locale = useLocale()
	const getTypeLabel = getQrTypeTranslation(t)

	const userTrial = getTrialRemainingTime(
		user?.createdAt,
		user?.flowData?.freeDaysQuota,
	)
	const isOnTrial = isUserOnFreeTrial(user) && !userTrial.expired
	const hasRowSelection = !!Object.keys(rowSelection).length

	useEffect(() => {
		deleteCookieClient(NEW_USER_COOKIE)
	}, [])

	const table = useReactTable<TQrData>({
		data,
		columns,
		sortingFns: {
			localeSort: (rowA, rowB, columnId) => {
				const rawA = String(rowA.getValue(columnId) ?? '')
				const rawB = String(rowB.getValue(columnId) ?? '')
				return compareSortValues(rawA, rawB, locale)
			},
			typeSort: (rowA, rowB, columnId) => {
				const rawA = rowA.getValue(columnId) as QR_TYPE | null | undefined
				const rawB = rowB.getValue(columnId) as QR_TYPE | null | undefined

				const labelA = getTypeLabel(rawA)
				const labelB = getTypeLabel(rawB)

				return compareSortValues(labelA, labelB, locale)
			},
			scansNumericSort,
		},
		getCoreRowModel: getCoreRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		enableSorting: true,
		onRowSelectionChange: setRowSelection,
		onColumnFiltersChange: setColumnFilters,
		onSortingChange: setSorting,
		getRowId: (row) => row.id,
		state: {
			rowSelection,
			columnFilters,
			sorting,
		},
		meta: {
			updateData: (rowIndex: number, columnId: string, value: unknown) => {
				setData((old) =>
					old.map((row, i) =>
						i === rowIndex ? { ...row, [columnId]: value } : row,
					),
				)
			},
		},
	})

	useHydrateAtoms([[tableAtom, table]])

	useEffect(() => {
		setTableAtom(table)
	}, [table, setTableAtom])

	// Effect to deselect only the QR codes that are not visible due to filtering
	useEffect(() => {
		const filteredRowIds = new Set(
			table.getFilteredRowModel().rows.map((row) => row.id),
		)
		const selectedRowIds = Object.keys(rowSelection)

		// Find selected rows that are no longer visible in the filtered results
		const rowsToDeselect = selectedRowIds.filter(
			(rowId) => !filteredRowIds.has(rowId),
		)

		// Only update selection if there are rows to deselect
		if (rowsToDeselect.length > 0) {
			const updatedSelection = { ...rowSelection }
			rowsToDeselect.forEach((rowId) => {
				delete updatedSelection[rowId]
			})
			setRowSelection(updatedSelection)
		}
	}, [rowSelection, setRowSelection, table])

	const hasFilter = table.getState().columnFilters.length > 0

	const isDesktop = useBreakpoint('desktop')

	// TODO: When covering table optimization, this can be improved to display desktop table during SSR, currently only mobile and tablet is included in SSR render and desktop only renders on hydration.
	const tableContent = isDesktop ? (
		<DesktopTableVirtualizedContent table={table} />
	) : (
		<MobileAndTabletVirtualizedContent table={table} />
	)

	return (
		<>
			<ShareQrDialog />
			<ScanQrDialog />
			<EditQrNameDialog />
			<EditQrUrlDialog />
			<QrPasswordDialogs />
			{/* TODO: fix this dialog breaking in Storybook */}
			<DownloadQrDialog />
			<ConfirmationDialog />
			<MobileActionsDialog />
			<DesktopActionsDialog />
			<div
				className={cn(
					'mx-auto flex w-full max-w-[1658px] flex-1 flex-col gap-4 overflow-visible desktop:gap-[22px]',
					isOnTrial && 'mb-10',
					hasRowSelection && 'mb-16',
				)}
			>
				<TableControls />
				<div className="flex min-h-0 flex-1 flex-col">
					{table.getRowModel().rows?.length ? (
						<div className="drop-shadow-generator">{tableContent}</div>
					) : hasFilter ? (
						<EmptyState />
					) : (
						<NoCodesState />
					)}
				</div>
				<TablePagination table={table} />
			</div>
			<BulkActionsToolbar />
		</>
	)
}
